import { useEffect, useRef, type RefObject } from "react";
import type { Attachment } from "@lumen/shared";
import type { RichTextInputHandle } from "./RichTextInput";

/** Preserve the draft across chat/workbench layouts and insert reviewed context. */
export function useComposerEditorContext({
  value,
  files,
  richInput,
  setValue,
  contextInsertion,
  onContextInsertionHandled,
  onDraftChange,
  onDraftAttachmentsChange,
}: {
  value: string;
  files: Attachment[];
  richInput: RefObject<RichTextInputHandle | null>;
  setValue: (value: string) => void;
  contextInsertion?: { id: number; text: string };
  onContextInsertionHandled?: (id: number) => void;
  onDraftChange?: (text: string) => void;
  onDraftAttachmentsChange?: (attachments: Attachment[]) => void;
}) {
  const draftChangeCallback = useRef(onDraftChange);
  const attachmentsChangeCallback = useRef(onDraftAttachmentsChange);
  draftChangeCallback.current = onDraftChange;
  attachmentsChangeCallback.current = onDraftAttachmentsChange;
  useEffect(() => {
    draftChangeCallback.current?.(value);
  }, [value]);
  useEffect(() => {
    attachmentsChangeCallback.current?.(files);
  }, [files]);
  const lastInsertion = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!contextInsertion || lastInsertion.current === contextInsertion.id)
      return;
    lastInsertion.current = contextInsertion.id;
    const current = richInput.current?.getMarkdown() ?? "";
    const next = current
      ? `${current}\n\n${contextInsertion.text}`
      : contextInsertion.text;
    setValue(next);
    richInput.current?.setMarkdown(next);
    richInput.current?.focus();
    onContextInsertionHandled?.(contextInsertion.id);
  }, [contextInsertion, onContextInsertionHandled, richInput, setValue]);
}
